import { useTranslation } from "@redotech/redo-i18n";
import {
  DECISION_LABEL_COLOR_OPTIONS,
  DecisionLabelColor,
} from "@redotech/redo-return-core-common/return-flow/decision-label-color";
import { RedoCompactBadgeSwatch } from "@redotech/redo-web/arbiter-components/badge/redo-badge";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import { decisionLabelBadgeColor } from "@redotech/redo-web/return-flow/decision-label-color-style";
import { Text } from "@redotech/redo-web/text";
import { memo, useMemo } from "react";
import * as styles from "./decision-label-color-select.module.css";

function colorLabel(
  color: DecisionLabelColor,
  t: (key: string) => string,
): string {
  switch (color) {
    case DecisionLabelColor.gray:
      return t("Grey");
    case DecisionLabelColor.red:
      return t("Red");
    case DecisionLabelColor.orange:
      return t("Orange");
    case DecisionLabelColor.yellow:
      return t("Yellow");
    case DecisionLabelColor.green:
      return t("Green");
    case DecisionLabelColor.blue:
      return t("Blue");
    case DecisionLabelColor.purple:
      return t("Purple");
    case DecisionLabelColor.pink:
      return t("Pink");
  }
}

function colorItems(
  useWrapper: boolean,
  t: (key: string) => string,
): RedoListItem<DecisionLabelColor>[] {
  return DECISION_LABEL_COLOR_OPTIONS.map((option) => ({
    type: "custom",
    id: option.color,
    value: option.color,
    useWrapper,
    render: (
      <Flex align="center" gap="sm" overflow="hidden">
        <RedoCompactBadgeSwatch
          color={decisionLabelBadgeColor[option.color]}
          shape="circle"
          type="alternate"
        />
        <Text fontSize="xs" overflow="hidden" whiteSpace="nowrap">
          {colorLabel(option.color, t)}
        </Text>
      </Flex>
    ),
  }));
}

export const DecisionLabelColorSelect = memo(function DecisionLabelColorSelect({
  label,
  value,
  onChange,
}: {
  label?: string;
  value: DecisionLabelColor;
  onChange: (color: DecisionLabelColor) => void;
}) {
  const { t } = useTranslation();
  const selected = DECISION_LABEL_COLOR_OPTIONS.find(
    (option) => option.color === value,
  );
  const menuOptions = useMemo(() => colorItems(true, t), [t]);
  const anchorOptions = useMemo(() => colorItems(false, t), [t]);
  return (
    <Flex className={styles.select} shrink={0}>
      <RedoSingleSelectDropdownInput<DecisionLabelColor>
        ariaLabel={
          selected
            ? t("Color: {{color}}", { color: colorLabel(selected.color, t) })
            : t("Color")
        }
        fitToAnchor={false}
        label={label}
        options={menuOptions}
        optionSelected={({ item }) => onChange(item.value)}
        selectedItem={anchorOptions.find((option) => option.value === value)}
        size="xs"
      />
    </Flex>
  );
});
